"use client"

import React, { useEffect, useState, Suspense } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'

export const dynamic = 'force-dynamic'

function SearchClient({ lng }: { lng: string }) {
  const searchParams = useSearchParams()
  const router = useRouter()
  const [q, setQ] = useState(searchParams.get('q') || '')
  const [results, setResults] = useState<Array<any>>([])
  const [loading, setLoading] = useState(false)

  const runSearch = async (query: string) => {
    if (!query.trim()) { setResults([]); return }
    setLoading(true)
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(query)}&lng=${encodeURIComponent(lng)}`)
      const json = await res.json()
      setResults(json.results || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const initial = searchParams.get('q')
    if (initial) runSearch(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.replace(`/${lng}/search?q=${encodeURIComponent(q)}`)
    runSearch(q)
  }

  return (
    <main className="container mx-auto px-4 py-10">
      <h1 className="text-2xl font-semibold">Search</h1>
      <form onSubmit={onSubmit} className="mt-4 flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search posts, tags, descriptions…"
          className="flex-1 rounded border border-gray-300 px-3 py-2"
        />
        <button className="rounded bg-teal-600 px-4 py-2 text-white hover:bg-teal-700">Search</button>
      </form>

      {loading && <p className="mt-6 text-gray-600">Searching…</p>}

      {!loading && results.length === 0 && q && (
        <p className="mt-6 text-gray-600">No results for “{q}”. Try a different term.</p>
      )}

      <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {results.map((r) => (
          <li key={r.slug} className="rounded border p-4">
            {r.image && (
              <div className="mb-3 overflow-hidden rounded">
                <Image
                  src={r.image}
                  alt={r.title}
                  width={320}
                  height={180}
                  className="h-auto w-full object-cover"
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="inline-block rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">
                {r.type || 'result'}
              </span>
              <h3 className="font-medium">
                <Link href={r.url || `/${lng}/posts/${r.slug}`} className="text-teal-700 hover:underline">
                  {r.title}
                </Link>
              </h3>
            </div>
            {r.description && <p className="mt-2 text-sm text-gray-600">{r.description}</p>}
          </li>
        ))}
      </ul>
    </main>
  )
}

export default function SearchPage({ params: { lng } }: { params: { lng: string } }) {
  return (
    <Suspense fallback={<main className="container mx-auto px-4 py-10"><p>Loading…</p></main>}>
      <SearchClient lng={lng} />
    </Suspense>
  )
}
